import React, { useEffect, useState } from 'react';
import { getDashboardStats, getRecentOrders } from '../api';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Package, ShoppingCart, CheckCircle, AlertCircle, Download } from 'lucide-react';
import { formatDate, getStatusColor, isCancelledOrder } from '../lib/utils';
import { toast } from 'sonner';
import ExportDialog from '../components/ExportDialog';
import useExportShortcut from '../hooks/useExportShortcut';
import { useScrollRestoration } from '../hooks/useScreenState';
import { exportToExcel, exportToPDF, EXPORT_COLUMNS } from '../utils/exportUtils';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const navigate = useNavigate();

  useScrollRestoration('dashboard', !loading);

  // ALT+E shortcut for export
  useExportShortcut(() => setExportDialogOpen(true));

  // Export handler
  const handleExport = async (format) => {
    if (!stats) {
      return { success: false, message: 'No data available to export.' };
    }

    const dashboardData = [
      { metric: 'Total Orders', value: stats.total_orders },
      { metric: 'Pending Orders', value: stats.pending_orders },
      { metric: 'Completed Orders', value: stats.completed_orders },
      { metric: 'Cancelled Orders', value: stats.cancelled_orders },
    ];

    if (format === 'excel') {
      return exportToExcel(dashboardData, EXPORT_COLUMNS.dashboard, 'Dashboard_Summary', 'Summary');
    } else {
      return exportToPDF(dashboardData, EXPORT_COLUMNS.dashboard, 'Dashboard_Summary', 'Dashboard Summary Report', []);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        getDashboardStats(),
        getRecentOrders(),
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-maroon-700">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 lg:space-y-8" data-testid="dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-maroon-950">
            Dashboard
          </h1>
          <p className="text-sm lg:text-base text-maroon-700 mt-1">Overview of your textile inventory</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            data-testid="export-button"
            variant="outline"
            onClick={() => setExportDialogOpen(true)}
            className="border-maroon-300 text-maroon-700 hover:bg-maroon-50 flex-1 sm:flex-none"
            title="Export (Alt+E)"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button
            data-testid="create-order-button"
            onClick={() => navigate('/orders/create')}
            className="bg-maroon-900 hover:bg-maroon-800 transition-colors duration-200 flex-1 sm:flex-none"
          >
            Create New Order
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
        <Card className="border-maroon-200 bg-white hover:shadow-md transition-shadow duration-300">
          <CardHeader className="flex flex-row items-center justify-between pb-1 lg:pb-2 p-3 lg:p-5">
            <CardTitle className="text-xs lg:text-base font-medium text-maroon-700">Total Orders</CardTitle>
            <Package className="h-4 w-4 lg:h-5 lg:w-5 text-maroon-600" />
          </CardHeader>
          <CardContent className="p-3 lg:p-5 pt-0">
            <div className="text-2xl lg:text-4xl font-bold text-maroon-950">{stats?.total_orders || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-maroon-200 bg-white hover:shadow-md transition-shadow duration-300">
          <CardHeader className="flex flex-row items-center justify-between pb-1 lg:pb-2 p-3 lg:p-5">
            <CardTitle className="text-xs lg:text-base font-medium text-maroon-700">Pending</CardTitle>
            <ShoppingCart className="h-4 w-4 lg:h-5 lg:w-5 text-yellow-600" />
          </CardHeader>
          <CardContent className="p-3 lg:p-5 pt-0">
            <div className="text-2xl lg:text-4xl font-bold text-yellow-700">{stats?.pending_orders || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-maroon-200 bg-white hover:shadow-md transition-shadow duration-300">
          <CardHeader className="flex flex-row items-center justify-between pb-1 lg:pb-2 p-3 lg:p-5">
            <CardTitle className="text-xs lg:text-base font-medium text-maroon-700">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 lg:h-5 lg:w-5 text-green-600" />
          </CardHeader>
          <CardContent className="p-3 lg:p-5 pt-0">
            <div className="text-2xl lg:text-4xl font-bold text-green-700">{stats?.completed_orders || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-maroon-200 bg-white hover:shadow-md transition-shadow duration-300">
          <CardHeader className="flex flex-row items-center justify-between pb-1 lg:pb-2 p-3 lg:p-5">
            <CardTitle className="text-xs lg:text-base font-medium text-maroon-700">Cancelled</CardTitle>
            <AlertCircle className="h-4 w-4 lg:h-5 lg:w-5 text-red-600" />
          </CardHeader>
          <CardContent className="p-3 lg:p-5 pt-0">
            <div className="text-2xl lg:text-4xl font-bold text-red-700">{stats?.cancelled_orders || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Orders */}
      <Card className="border-maroon-200 bg-white">
        <CardHeader className="p-4 lg:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-lg lg:text-2xl font-semibold text-maroon-950">Recent Orders</CardTitle>
            <Button
              variant="outline"
              onClick={() => navigate('/orders')}
              className="border-maroon-300 text-maroon-700 hover:bg-maroon-50 transition-colors duration-200 text-sm"
            >
              View All Orders
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 lg:p-5 pt-0">
          <div className="space-y-3">
            {recentOrders.length === 0 ? (
              <p className="text-center text-maroon-500 py-8">No orders yet. Create your first order!</p>
            ) : (
              recentOrders.map((order) => {
                const isCancelled = isCancelledOrder(order.order_details.status);
                return (
                <div
                  key={order.id}
                  className={`flex items-center justify-between p-4 border rounded-lg transition-colors duration-200 cursor-pointer ${isCancelled ? 'bg-red-100 border-red-300 hover:bg-red-200' : 'border-maroon-200 hover:bg-maroon-50'}`}
                  onClick={() => navigate(`/orders/${order.id}`)}
                  data-testid={`order-item-${order.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <p className={`font-semibold ${isCancelled ? 'text-red-800' : 'text-maroon-950'}`}>
                        {order.order_details.indent_number}
                      </p>
                      <span className={`px-2 py-1 text-xs rounded-md border ${getStatusColor(order.order_details.status)}`}>
                        {order.order_details.status}
                      </span>
                    </div>
                    <p className={`text-sm mt-1 ${isCancelled ? 'text-red-600' : 'text-maroon-600'}`}>
                      {formatDate(order.order_details.order_date)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-medium ${isCancelled ? 'text-red-800' : 'text-maroon-950'}`}>
                      {order.buyers.length} Buyer{order.buyers.length !== 1 ? 's' : ''}
                    </p>
                    <p className={`text-sm ${isCancelled ? 'text-red-600' : 'text-maroon-600'}`}>
                      {order.suppliers.length} Supplier{order.suppliers.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        onExport={handleExport}
        title="Export Dashboard"
        hasFilters={false}
        filterDescription=""
        totalRecords={4}
        filteredRecords={4}
      />
    </div>
  );
}
